import { Check, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const FeatureList = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((f) => (
      <li key={f} className="flex items-start gap-2.5 text-sm text-slate-600">
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-deep" />
        {f}
      </li>
    ))}
  </ul>
);

export default function Services() {
  return (
    <section id="services" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-gold-deep">
            What we do
          </p>
          <h2 className="mt-3 max-w-2xl font-display text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl">
            Two services. One focus.
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12">
          <Reveal className="md:col-span-7" delay={0.05}>
            <article
              data-testid="service-card-testing"
              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-xl"
            >
              <div className="relative h-56 overflow-hidden sm:h-64">
                <img
                  src="https://images.pexels.com/photos/8293640/pexels-photo-8293640.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Certified technician inspecting a Boston area home"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-navy shadow">
                  For home sales &amp; annual checks
                </span>
              </div>
              <div className="flex flex-1 flex-col p-6 sm:p-8">
                <h3 className="font-display text-xl font-semibold text-navy sm:text-2xl">
                  Continuous Radon Testing
                </h3>
                <p className="mt-3 text-slate-600">
                  A calibrated continuous radon monitor placed in your home,
                  recording hourly readings and returning a certified report.
                  The gold standard for real-estate closings and peace of mind.
                </p>
                <div className="mt-5 flex-1">
                  <FeatureList
                    items={[
                      "Continuous monitor with hourly readings",
                      "Certified report for closings & records",
                      "Sub-slab and well-water evaluation available",
                      "Clear explanation of your results, in plain English",
                    ]}
                  />
                </div>
                <a
                  data-testid="service-testing-cta"
                  href="#booking"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-gold-deep transition-colors hover:text-navy"
                >
                  Book a test <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </article>
          </Reveal>

          <Reveal className="md:col-span-5" delay={0.12}>
            <article
              data-testid="service-card-mitigation"
              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-xl"
            >
              <div className="relative h-56 overflow-hidden sm:h-64">
                <img
                  src="https://images.pexels.com/photos/4092026/pexels-photo-4092026.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Basement foundation where a radon mitigation system would be installed"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-navy shadow">
                  If your level is 4.0 pCi/L or higher
                </span>
              </div>
              <div className="flex flex-1 flex-col p-6 sm:p-8">
                <h3 className="font-display text-xl font-semibold text-navy sm:text-2xl">
                  Radon Mitigation Systems
                </h3>
                <p className="mt-3 text-slate-600">
                  Custom sub-slab depressurization, engineered for your
                  foundation — from historic fieldstone basements to modern
                  slabs — and built to bring radon below the EPA action level.
                </p>
                <div className="mt-5 flex-1">
                  <FeatureList
                    items={[
                      "Custom-designed for your foundation",
                      "Quiet, continuous fan systems",
                      "Neat roofline venting, inside and out",
                      "Post-mitigation retest to verify results",
                    ]}
                  />
                </div>
                <a
                  data-testid="service-mitigation-cta"
                  href="#booking"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-gold-deep transition-colors hover:text-navy"
                >
                  Request a free quote <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </article>
          </Reveal>

          <Reveal className="md:col-span-5" delay={0.05}>
            <article
              data-testid="service-card-realestate"
              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-xl"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/33448111/pexels-photo-33448111.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Historic Boston brick rowhomes"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-navy shadow">
                  Buyers &amp; sellers
                </span>
              </div>
              <div className="flex flex-1 flex-col p-6 sm:p-8">
                <h3 className="font-display text-xl font-semibold text-navy sm:text-2xl">
                  Real Estate Closing Protocol
                </h3>
                <p className="mt-3 flex-1 text-slate-600">
                  Tight closing timeline? We coordinate directly with realtors,
                  attorneys and lenders, and deliver a certified radon report on
                  completion — without the scramble.
                </p>
                <a
                  data-testid="service-realestate-cta"
                  href="#booking"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-gold-deep transition-colors hover:text-navy"
                >
                  Get started <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </article>
          </Reveal>

          <Reveal className="md:col-span-7" delay={0.12}>
            <div
              data-testid="service-start-card"
              className="flex h-full flex-col justify-between rounded-2xl bg-navy p-6 sm:p-8"
            >
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.22em] text-gold-light">
                  Not sure where to start?
                </p>
                <h3 className="mt-3 font-display text-xl font-semibold text-white sm:text-2xl">
                  Start with a test — it takes the guesswork out.
                </h3>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-white/70">
                  If you have never tested your home — or it has been a few
                  years — a short test is all it takes to know where you stand.
                  Radon is invisible and odorless; the test is neither
                  complicated nor disruptive.
                </p>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <a
                  data-testid="service-start-book-btn"
                  href="#booking"
                  className="rounded-lg bg-white px-6 py-3 text-sm font-semibold text-navy transition-all hover:-translate-y-0.5 hover:text-gold-deep"
                >
                  Book a Radon Test
                </a>
                <a
                  href="tel:+15089692104"
                  className="text-sm font-semibold text-gold-light transition-colors hover:text-white"
                >
                  or call (508) 969-2104
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
